import { Box, Typography } from '@mui/material';
import { type ReactNode, useId } from 'react';
import CustomMenu from '@/components/menu/menu';

type PreferenceSelectOption<T extends string | number> = {
  value: T;
  label: ReactNode;
};

type PreferenceSelectProps<T extends string | number> = {
  label: ReactNode;
  value: T;
  options: readonly PreferenceSelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  id?: string;
};

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  width: '100%',
  minHeight: 36,
} as const;

const buttonSx = {
  minWidth: 0,
  maxWidth: '70%',
  overflow: 'hidden',
  height: 36,
  px: 1,
  fontSize: '0.75rem',
  lineHeight: '1rem',
  justifyContent: 'space-between',
  textAlign: 'left',
  color: 'text.primary',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  '& .MuiButton-endIcon': {
    ml: 0.5,
    flexShrink: 0,
    color: 'text.secondary',
  },
  '& .MuiButton-endIcon svg': {
    fontSize: 16,
  },
} as const;

const valueSx = {
  // Flex child of the Button root: without minWidth it won't shrink, so the ellipsis never fires.
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '0.75rem',
  '& .MuiTypography-root': {
    fontSize: 'inherit',
  },
} as const;

const menuAnchor = { vertical: 'bottom', horizontal: 'right' } as const;
const menuTransform = { vertical: 'top', horizontal: 'right' } as const;

export function PreferenceSelect<T extends string | number>({
  label,
  value,
  options,
  onChange,
  disabled,
  id,
}: PreferenceSelectProps<T>) {
  const fallbackId = useId();
  const triggerId = id ?? fallbackId;
  const labelId = `${triggerId}-label`;

  const items = options.map((option) => ({
    id: String(option.value),
    value: option.value,
    label: option.label,
  }));

  return (
    <Box sx={rowSx}>
      <Typography id={labelId} variant="body2" color={disabled ? 'text.disabled' : undefined} component="span">
        {label}
      </Typography>
      <CustomMenu
        value={String(value)}
        items={items}
        renderItem={(item) => item.label}
        renderLabel={(itemId) => (
          <Box component="span" sx={valueSx}>
            {items.find((item) => item.id === itemId)?.label ?? itemId}
          </Box>
        )}
        onSelect={(item) => onChange(item.value)}
        buttonProps={{
          id: triggerId,
          // Label + self, as MUI's Select does it: the row label names the control, the trigger supplies the value.
          'aria-labelledby': `${labelId} ${triggerId}`,
          size: 'medium',
          variant: 'text',
          color: 'inherit',
          disabled,
          sx: buttonSx,
        }}
        menuProps={{
          disableScrollLock: true,
          // Transparent, not click-through: switching between two open selects takes two clicks.
          // Accepted — MUI's own Select renders the same modal Menu and behaves identically.
          slotProps: { backdrop: { invisible: true } },
          anchorOrigin: menuAnchor,
          transformOrigin: menuTransform,
        }}
      />
    </Box>
  );
}
